export const CLASS_1A_DISCLOSURE = 'Class 1a documentation in progress; all approvals are site-specific.';

export type SolutionPage = {
  path: string;
  title: string;
  description: string;
  h1: string;
  eyebrow: string;
  intro: string;
  image: string;
  imageAlt: string;
  audience: string;
  model?: string;
  useCategory: 'residential' | 'adventure' | 'resort' | 'office-studio';
  floorArea?: string;
  amenities: string[];
  disclosure?: string;
  benefits: { title: string; text: string }[];
  process: { title: string; text: string }[];
  faq: { question: string; answer: string }[];
};

export const SOLUTION_PAGES: SolutionPage[] = [
  {
    path: '/granny-flats-victoria',
    title: 'Granny Flats Victoria | Modular Second Homes | Nexa',
    description: 'Explore modular granny flats for Victorian properties, led by the self-contained M5 Residential with site assessment and approval support.',
    h1: 'Modular Granny Flats for Victoria',
    eyebrow: 'Small Second Homes',
    intro: 'A compact, self-contained second home designed around real residential amenity and a clear site-specific approval pathway.',
    image: '/images/7f3649eeefc8.webp',
    imageAlt: 'M5 modular granny flat in a landscaped Victorian backyard',
    audience: 'Homeowners and families planning a small second home on an established Victorian residential property.',
    model: 'M5 Residential',
    useCategory: 'residential',
    floorArea: '26 m²',
    amenities: ['Complete kitchen', 'Bathroom', 'Toilet', 'Laundry'],
    disclosure: CLASS_1A_DISCLOSURE,
    benefits: [
      { title: 'Self-contained living', text: 'A practical residential configuration with the essential facilities for independent everyday use.' },
      { title: 'Compact footprint', text: 'The 26 m² M5 format makes efficient use of a backyard while preserving a comfortable interior.' },
      { title: 'Site-first planning', text: 'Access, services, foundations and approvals are reviewed for the individual property before a proposal is finalised.' },
    ],
    process: [
      { title: 'Share your site', text: 'Send the address, photos, access information and intended use.' },
      { title: 'Feasibility review', text: 'We review siting, delivery access, service connections and the likely project scope.' },
      { title: 'Configure the M5', text: 'Confirm the residential layout, finishes and required inclusions.' },
      { title: 'Plan delivery', text: 'Coordinate documentation, site works, transport, installation and the required approvals.' },
    ],
    faq: [
      { question: 'Is a building permit required for a small second home in Victoria?', answer: 'A building permit is required. Planning and other requirements depend on the property, proposal and relevant authorities, so every site needs its own assessment.' },
      { question: 'What makes the M5 Residential self-contained?', answer: 'The residential configuration includes a complete kitchen, bathroom, toilet and laundry within its 26 m² floor area.' },
      { question: 'Is there one installed price for every site?', answer: 'No. The pod, transport, foundation, installation, service connections and approval costs depend on the site and selected configuration.' },
    ],
  },
  {
    path: '/m5-residential-granny-flat',
    title: 'M5 Residential Granny Flat Victoria | Nexa Pods',
    description: 'Meet the 26 m² M5 Residential granny flat with a complete kitchen, bathroom, toilet and laundry for site-assessed projects in Victoria.',
    h1: 'M5 Residential Granny Flat',
    eyebrow: '26 m² Residential Pod',
    intro: 'A dedicated residential version of the M5, configured as a complete small second home rather than an accommodation-only pod.',
    image: '/images/b7c34894f8a7.webp',
    imageAlt: 'Interior of the M5 Residential modular granny flat',
    audience: 'Victorian households looking for an independent family, guest or multigenerational living space.',
    model: 'M5 Residential',
    useCategory: 'residential',
    floorArea: '26 m²',
    amenities: ['Complete kitchen', 'Bathroom', 'Toilet', 'Laundry'],
    disclosure: CLASS_1A_DISCLOSURE,
    benefits: [
      { title: 'Residential configuration', text: 'Kitchen, bathroom, toilet and laundry are planned together for a genuinely self-contained interior.' },
      { title: 'M5 proportions', text: 'A 7.56 m × 3.6 m module provides 26 m² of internal floor area for one to two occupants.' },
      { title: 'Transparent project scope', text: 'Pod supply is separated from transport, foundations, installation, services and approval-related work.' },
    ],
    process: [
      { title: 'Site enquiry', text: 'Tell us about the property, intended occupants and preferred timing.' },
      { title: 'Project assessment', text: 'Review access, siting, connections, foundations and site-specific approval needs.' },
      { title: 'Residential selections', text: 'Confirm the internal arrangement, finishes, fixtures and optional upgrades.' },
      { title: 'Coordinated proposal', text: 'Receive a project scope that distinguishes the pod from site and delivery costs.' },
    ],
    faq: [
      { question: 'Does M5 Residential include a kitchen and laundry?', answer: 'Yes. Its residential configuration includes a complete kitchen, bathroom, toilet and laundry.' },
      { question: 'How large is M5 Residential?', answer: 'The M5 has a 26 m² floor area and external dimensions of approximately 7.56 m by 3.6 m by 3.2 m.' },
      { question: 'Is the displayed adventure-model price an installed residential price?', answer: 'No. Residential specifications and site costs differ, so an accurate proposal is prepared after the site and selected inclusions are reviewed.' },
    ],
  },
  {
    path: '/glamping-pods-australia',
    title: 'Luxury Glamping Pods Australia | Nexa Pods',
    description: 'Create distinctive stays with panoramic modular glamping pods for Australian tourism, short-stay and remote accommodation projects.',
    h1: 'Luxury Glamping Pods Australia',
    eyebrow: 'Destination Accommodation',
    intro: 'High-impact modular accommodation for landscapes where the view, guest experience and speed of project delivery all matter.',
    image: '/images/ai-luxury-glamping-resort-in-pristine-mount-950b56.webp',
    imageAlt: 'Luxury Nexa glamping pods in an Australian landscape',
    audience: 'Tourism operators, landowners and short-stay developers planning premium destination accommodation across Australia.',
    model: 'M5 Adventure',
    useCategory: 'adventure',
    amenities: ['Panoramic glazing', 'Private bathroom options', 'Climate control', 'Interior fit-out options'],
    benefits: [
      { title: 'A memorable stay', text: 'Sculpted forms and broad glazing give guests a clear sense of place from the moment they arrive.' },
      { title: 'Repeatable rollout', text: 'A modular product family supports staged sites and a consistent guest experience across multiple units.' },
      { title: 'Remote-site thinking', text: 'Transport, foundations, services and access are considered early for regional and remote locations.' },
    ],
    process: [
      { title: 'Define the experience', text: 'Clarify guest profile, location, capacity, amenities and commercial goals.' },
      { title: 'Review the site', text: 'Assess access, siting, views, services and project constraints.' },
      { title: 'Select and configure', text: 'Choose the model, fit-out and finishes that support the target stay.' },
      { title: 'Plan the rollout', text: 'Coordinate supply, transport, site preparation and installation sequencing.' },
    ],
    faq: [
      { question: 'Which Nexa model suits a premium glamping stay?', answer: 'M5 Adventure is a strong starting point for one to two guests, while larger models can support more space or a different amenity mix.' },
      { question: 'Can glamping pods be used in regional Australia?', answer: 'They can be considered for regional and remote projects, subject to transport access, foundations, services and all site-specific approvals.' },
      { question: 'Can a project begin with only a few pods?', answer: 'Yes. Modular projects can be planned in stages when the site layout, services and operating model are designed for future expansion.' },
    ],
  },
  {
    path: '/modular-cabins-for-resorts',
    title: 'Modular Cabins for Resorts Australia | Nexa Pods',
    description: 'Plan scalable resort accommodation with premium modular cabins, coordinated model selection, site logistics and guest-experience design.',
    h1: 'Modular Cabins for Resorts',
    eyebrow: 'Hospitality Development',
    intro: 'A scalable accommodation platform for resort operators who need a distinctive guest product and a practical multi-unit rollout.',
    image: '/images/ai-multiple-futuristic-space-capsule-pods-a-ac785a.webp',
    imageAlt: 'Multiple modular Nexa cabins arranged for a resort',
    audience: 'Resort owners, developers and operators planning new inventory, site extensions or staged accommodation precincts in Australia.',
    useCategory: 'resort',
    amenities: ['Model and layout selection', 'Guest amenity options', 'Finish packages', 'Staged rollout planning'],
    benefits: [
      { title: 'Consistent product', text: 'Repeatable cabins support a coherent guest experience while allowing model and finish variations.' },
      { title: 'Portfolio planning', text: 'Mix unit types and capacities around the site, operating model and target room rate.' },
      { title: 'Delivery coordination', text: 'Bring transport routes, crane access, foundations and services into the design conversation early.' },
    ],
    process: [
      { title: 'Commercial brief', text: 'Align the accommodation mix with guests, operations, site capacity and investment goals.' },
      { title: 'Site and logistics', text: 'Review unit placement, views, circulation, access, services and installation needs.' },
      { title: 'Product schedule', text: 'Confirm model quantities, amenity levels, finishes and delivery stages.' },
      { title: 'Implementation plan', text: 'Coordinate manufacturing, site preparation, transport and installation milestones.' },
    ],
    faq: [
      { question: 'Can a resort use more than one pod model?', answer: 'Yes. A project can combine models to create different capacities, experiences and price points, subject to the site plan and operational brief.' },
      { question: 'Does Nexa provide a site-specific proposal?', answer: 'Yes. A useful proposal considers unit supply alongside access, transport, foundations, services, installation and required approvals.' },
      { question: 'Are modular cabins suitable for staged expansion?', answer: 'They can support staged delivery when the master plan, infrastructure and logistics allow later units to be added efficiently.' },
    ],
  },
  {
    path: '/backyard-office-pods-melbourne',
    title: 'Backyard Office Pods Melbourne | A-S Elite | Nexa',
    description: 'Create a separate Melbourne backyard office or studio with the compact A-S Elite pod, planned around access, power, data and daily work.',
    h1: 'Backyard Office Pods Melbourne',
    eyebrow: 'A-S Elite Workspace',
    intro: 'A compact, purpose-focused room that creates separation between work and home without taking over the backyard.',
    image: '/images/ai-modern-futuristic-backyard-office-pod-in-3b9190.webp',
    imageAlt: 'A-S Elite backyard office pod in Melbourne',
    audience: 'Melbourne homeowners, consultants and small businesses seeking a dedicated office, studio or meeting space.',
    model: 'A-S Elite',
    useCategory: 'office-studio',
    amenities: ['Power and lighting provision', 'Heating and cooling options', 'Data connectivity planning', 'Workspace fit-out options'],
    benefits: [
      { title: 'A focused work zone', text: 'Create physical separation from household activity while keeping the commute to a few steps.' },
      { title: 'Compact footprint', text: 'A-S Elite is intended for office and studio use where space efficiency is central to the brief.' },
      { title: 'Planned connections', text: 'Access, electrical supply, data and the selected fit-out are considered as one practical workspace.' },
    ],
    process: [
      { title: 'Describe the work', text: 'Tell us who will use the space, the equipment involved and the preferred layout.' },
      { title: 'Check the backyard', text: 'Review access, siting, power, data and any property-specific constraints.' },
      { title: 'Configure A-S Elite', text: 'Select finishes, climate control, lighting and workspace inclusions.' },
      { title: 'Plan installation', text: 'Confirm the supply scope, site work, transport and installation sequence.' },
    ],
    faq: [
      { question: 'What is A-S Elite designed for?', answer: 'A-S Elite is a compact office or studio pod for focused work, creative use or small meetings.' },
      { question: 'Can the office include power, data and climate control?', answer: 'These requirements can be planned as part of the selected configuration and site connection scope.' },
      { question: 'Does every backyard suit the same installation method?', answer: 'No. Access, foundations, setbacks, services and local requirements need to be reviewed for the individual property.' },
    ],
  },
];

export const getSolutionPage = (pathname: string) => SOLUTION_PAGES.find((page) => page.path === pathname);
